import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import infoPageService, { InfoPage } from '../../services/infoPageService';
import { t } from '../../i18n';

// ============================================
// "Darts-Wissen" – horizontale Scroll-Sektion auf der Startseite.
// KI-gefüllte Info-Pages (z. B. „9-Darter") als Karten im gleichen Stil
// wie HomeNewTournaments. Klick → /info/:slug, Header-Link → /info.
// Blendet sich bei leeren Daten aus.
// ============================================

const ACCENT = '#3fced1';
const MAX_ITEMS = 10;

const HomeDartsKnowledge: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<InfoPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    infoPageService
      .getAllPages()
      .then((list) => {
        if (!cancelled) {
          setPages((list || []).filter((p) => p && p.slug).slice(0, MAX_ITEMS));
        }
      })
      .catch(() => {
        /* Section bleibt einfach ausgeblendet */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (!loading && pages.length === 0) return null;

  return (
    <section aria-label={t('web.component.home-darts-knowledge.darts-wissen')}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-darts-knowledge.darts-wissen')}</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/info')}
            className="mr-1 hidden items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1] sm:flex"
          >
            {t('web.component.events-filter-bar.alle')}{' '}<ArrowRight size={13} />
          </button>
          {!loading && pages.length > 3 && (
            <>
              <button
                type="button"
                aria-label={t('web.component.chats-manager.zurueck')}
                onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                aria-label={t('web.component.home-darts-knowledge.weiter')}
                onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[240px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : pages.map((page) => (
              <KnowledgeCard
                key={page.id || page.slug}
                page={page}
                onClick={() => navigate(`/info/${page.slug}`)}
              />
            ))}
      </div>
    </section>
  );
};

const KnowledgeCard: React.FC<{ page: InfoPage; onClick: () => void }> = ({ page, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const thumb = infoPageService.buildFileUrl(page.thumbnail || page.heroImage);
  const showImage = thumb && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={thumb as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <img
              src="/icon.png"
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-lg shadow-[0_0_24px_rgba(63,206,210,0.25)]"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2.5">
        {/* Kategorie-Chip über dem Titel */}
        <span
          className="mb-2 self-start rounded px-2 py-0.5 text-[10px] font-bold text-black"
          style={{ backgroundColor: ACCENT }}
        >
          {page.category || t('web.component.home-darts-knowledge.wissen')}
        </span>
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {page.title || t('web.component.forum-compact-list.ohne-titel')}
        </h3>
        {page.intro && (
          <p className="mt-auto line-clamp-2 pt-1.5 text-[11px] leading-snug text-gray-500">
            {page.intro}
          </p>
        )}
      </div>
    </button>
  );
};

export default HomeDartsKnowledge;
